const { PAYMENT_TYPE, PAYMENT_STATUS } = require('arbitrip-common/general/constants');
const { RESERVATION_STATUS } = require('@arbitrip/constants/reservations');
const { BANK_TRANSFER_BUSINESS_DAYS_BEFORE_DEADLINE } = require('@arbitrip/constants');
const { subtractBusinessDays, countBusinessDaysUntil } = require('arbitrip-common/general/utils/BusinessDays');

function getIsUnpaid(profile, booking) {
	if (!profile || !booking) {
		console.log('nop');
		return false;
	}

	return (
		booking.status === RESERVATION_STATUS.APPROVED &&
		profile.pay_later_allowed &&
		booking?.pay_later_deadline &&
		booking?.payment?.payment_type === PAYMENT_TYPE.PAY_LATER &&
		booking?.payment?.status !== PAYMENT_STATUS.BILLED
	);
}

function getIsBnpl(booking) {
	if (!booking) return false;
	return booking?.payment?.payment_type === PAYMENT_TYPE.BNPL;
}

function getBankTransferDeadline(booking) {
	const payLaterDeadline = booking?.pay_later_deadline;
	if (!payLaterDeadline) return null;
	return subtractBusinessDays(payLaterDeadline, BANK_TRANSFER_BUSINESS_DAYS_BEFORE_DEADLINE).toDate();
}

function isBankTransferAvailable(booking) {
	const payLaterDeadline = booking?.pay_later_deadline;
	return !!payLaterDeadline && countBusinessDaysUntil(payLaterDeadline) >= BANK_TRANSFER_BUSINESS_DAYS_BEFORE_DEADLINE;
}

/**
 * Compute the points-adjusted BNPL amounts from the server's ILS base.
 *
 * The caller owns how the points discount (in ILS) is derived, since the
 * available inputs differ per surface (chosen points × rate, applied discount
 * value, exact points applied, …). This util owns the single source of truth
 * for converting the remaining ILS base into the displayed pay-now / pay-later
 * amounts in both ILS and USD.
 *
 * `pointIlsValue` is the per-point ILS value used to decide `isFullyCovered`:
 * fully covered means the remaining base is below one point's worth (no further
 * point can reduce it) — i.e. the real-zero / full-deal-limit case, as opposed
 * to a value that merely rounds to zero.
 *
 * @returns {null|{adjustedIls:number, isFullyCovered:boolean, payNowIls:number, payLaterIls:number, payNowUsd:(number|null), payLaterUsd:(number|null)}}
 *   `null` when there is no ILS base. USD amounts are `null` when no ILS→USD
 *   rate is available (callers fall back to the server's USD figures).
 */
function computeBnplAmounts({
	bnplIlsAmount,
	bnplIlsRate,
	bnplFeePercentage = 2,
	pointsDiscountIls = 0,
	pointIlsValue = 0,
} = {}) {
	if (bnplIlsAmount == null) {
		return null;
	}
	const adjustedIls = Math.max(0, bnplIlsAmount - (pointsDiscountIls || 0));
	const isFullyCovered = adjustedIls <= 0 || (pointsDiscountIls > 0 && pointIlsValue > 0 && adjustedIls < pointIlsValue);
	const feeMultiplier = 1 + bnplFeePercentage / 100;
	const hasRate = bnplIlsRate > 0;
	return {
		adjustedIls,
		isFullyCovered,
		payNowIls: Math.round(adjustedIls),
		payLaterIls: Math.round(adjustedIls * feeMultiplier),
		payNowUsd: hasRate ? Math.round(adjustedIls / bnplIlsRate) : null,
		payLaterUsd: hasRate ? Math.round((adjustedIls * feeMultiplier) / bnplIlsRate) : null,
	};
}

module.exports = {
	getIsUnpaid,
	getIsBnpl,
	computeBnplAmounts,
	subtractBusinessDays,
	getBankTransferDeadline,
	isBankTransferAvailable,
};
